import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useWebSocket } from '@/contexts/WebSocketContext';
import {
  LayoutDashboard,
  Building2,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  User,
  Shield,
  CreditCard,
  Receipt,
  Smartphone,
  Bell,
  HelpCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import NotificationsMenu from '@/components/common/NotificationsMenu';
import {
  getSuperAdminNotificationRoute,
  announceNotificationsChanged,
  onNotificationsChanged,
} from "@/lib/notifications";
import {
  getNotifications,
  MarkAsALLReadNotifications,
  MarkAsReadNotificationbyId,
  deleteAllNotification,
} from '@/services/employeeService';

const SuperAdminLayout = () => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const { addNotificationListener } = useWebSocket();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchNotifications();

    const unsubscribe = addNotificationListener((newNotification) => {
      setNotifications((prev) => [newNotification, ...prev]);
      toast.info(newNotification.title, {
        description: newNotification.message,
      });
    });
    return () => unsubscribe();
  }, []);

  // The Notifications page (or another bell) changed read / cleared state.
  useEffect(() => onNotificationsChanged(() => fetchNotifications()), []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await getNotifications();

      const sortedNotifications = response.data.sort((a, b) => {
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      setNotifications(sortedNotifications);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAllAsRead = async () => {
    try {
      await MarkAsALLReadNotifications();
      toast.success("All notifications marked as read");
      announceNotificationsChanged();
      setNotifications(notifications.map((n) => ({ ...n, is_read: true })));
      if (window.refreshSidebarNotifications) {
        window.refreshSidebarNotifications();
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      toast.error("Failed to mark notifications as read");
    }
  };

  const clearAllNotifications = async () => {
    try {
      await deleteAllNotification();
      setNotifications([]);
      toast.success("All notifications cleared");
      announceNotificationsChanged();
    } catch (error) {
      console.error("Error clearing all notifications:", error);
      toast.error("Failed to clear notifications");
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read first
    try {
      await MarkAsReadNotificationbyId(notification._id, true);
      setNotifications((prev) =>
        prev.map((n) =>
          n._id === notification._id ? { ...n, is_read: true } : n
        )
      );
      announceNotificationsChanged();
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }

    // Where to go: shared with the Notifications page (lib/notifications.js).
    const route = getSuperAdminNotificationRoute(notification);
    if (route?.path) {
      navigate(route.path, route.state ? { state: route.state } : undefined);
    }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const navigation = [
    { name: 'Dashboard', href: '/super-admin/dashboard', icon: LayoutDashboard },
    { name: 'Companies', href: '/super-admin/companies', icon: Building2 },
    { name: 'Plans', href: '/super-admin/plans', icon: CreditCard },
    { name: 'Payments', href: '/super-admin/payments', icon: Receipt },
    { name: 'App Versions', href: '/super-admin/app-versions', icon: Smartphone },
    { name: 'Send Notification', href: '/super-admin/notifications', icon: Bell },
  ];

  const isActivePath = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-3">
              <img
                src="/zenith-logo-no-name.png"
                alt="Zenith Logo"
                className="w-auto h-8"
              />
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground">
                Zenith
              </h1>
              <p className="text-xs text-muted-foreground">
                Super Admin
              </p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-muted-foreground hover:text-foreground dark:text-muted-foreground dark:hover:text-foreground"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1">
          {navigation.map((item) => {
            const Icon = item.icon;
            const active = isActivePath(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors text-foreground ${
                  active
                    ? "bg-primary/10 text-primary hover:bg-primary/20"
                    : "hover:bg-accent hover:text-accent-foreground"
                }`}
              >
                <Icon className="h-5 w-5 mr-3" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="px-4 py-4 border-t border-border">
          <div className="flex items-center px-4 py-2 rounded-lg bg-muted min-w-0">
            <div className="flex items-center space-x-3 min-w-0 w-full">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                <User className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground truncate">
                  Super Admin
                </p>
                <p
                  className="text-xs text-muted-foreground truncate max-w-full"
                  title={user?.email}
                >
                  {user?.email}
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-40 bg-card border-b border-border">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-muted-foreground hover:text-foreground dark:text-muted-foreground dark:hover:text-foreground"
            >
              <Menu className="h-6 w-6" />
            </button>

            {/* Page Title */}
            <div className="flex-1 lg:flex-none">
              <h2 className="text-xl font-semibold text-foreground">
                {navigation.find((item) => isActivePath(item.href))?.name ||
                  "Super Admin"}
              </h2>
            </div>

            {/* Right Section */}
            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <NotificationsMenu
                notifications={notifications}
                unreadCount={unreadCount}
                loading={loading}
                onNotificationClick={handleNotificationClick}
                onMarkAll={markAllAsRead}
                onClearAll={clearAllNotifications}
                onViewAll={() => navigate("/super-admin/inbox")}
                triggerClassName="relative h-8 w-8"
              />

              {/* Help Center */}
              <button
                onClick={() => navigate('/help')}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted dark:text-muted-foreground dark:hover:text-foreground dark:hover:bg-muted transition-colors"
                title="Help Center"
                data-testid="help-center-btn"
              >
                <HelpCircle className="h-5 w-5" />
              </button>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted dark:text-muted-foreground dark:hover:text-foreground dark:hover:bg-muted transition-colors"
                title={
                  isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"
                }
              >
                {isDarkMode ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </button>

              {/* Logout Button */}
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default SuperAdminLayout;
